import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import ConceptCanvas from "./ConceptCanvas.js";
import ListenButton from "../LessonAudio/ListenButton";
import PaneResizer, { clampSize } from "./PaneResizer.js";
import { LiveStatus, useStatus, useOnChange, refocusIfLost } from "./workspaceA11y.js";
import "./PromptLabLayout.css";
import "./CanvasLayout.css";
import { AiFirstUseNotice, AiMistakesNote } from "../AiNotice/AiNotice";

/**
 * Module 1 ("canvas") layout:
 *
 *   +------------+‖+-----------------------------+
 *   |            |‖|   Concept Canvas (exercise) |
 *   |  Lesson    |‖+=========== drag ============+
 *   |  markdown  |‖|   Prompt Lab (optional)     |
 *   +------------+‖+-----------------------------+
 *              drag ↑ (vertical)      ↑ horizontal
 *
 * Both borders are draggable split-pane dividers (`.pl-resizer`): the
 * vertical one resizes Lesson vs. the studio column; the horizontal one resizes
 * the Concept Canvas vs. the Prompt Lab. They are plain pointer-driven dividers
 * (not react-resizable) so the WHOLE border is the drag target and it can't be
 * shadowed by the lesson pane's scrollbar. Both also move with the arrow keys
 * (see PaneResizer).
 *
 * The Concept Canvas IS the exercise and carries its own light self-check — it
 * reports completion through `onComplete`. The Prompt Lab is an OPTIONAL helper:
 * a thinking partner the learner can ask questions of at any time. It never
 * grades anything and is never required.
 */
const CanvasLayout = ({
    definition,
    lessonTitle,
    messages,
    input,
    setInput,
    onSend,
    isThinking,
    onComplete,
    allPassed,
    onCanvasStateChange,
    solveSignal,
}) => {
    const { t } = useTranslation();
    const [leftWidth, setLeftWidth] = useState(420);
    const [canvasHeight, setCanvasHeight] = useState(320);
    const containerRef = useRef(null);
    const rightColRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const sendHintId = useId();

    // Initial split sizes, recomputed on window resize.
    useEffect(() => {
        const setInitial = () => {
            const el = containerRef.current;
            const totalW = el ? el.clientWidth : window.innerWidth;
            const totalH = el ? el.clientHeight : window.innerHeight;
            setLeftWidth(Math.min(520, Math.max(320, Math.floor(totalW * 0.36))));
            // Leave at least ~200px for the Prompt Lab chat below the canvas.
            setCanvasHeight(Math.max(160, Math.min(totalH - 210, Math.floor(totalH * 0.46))));
        };
        setInitial();
        window.addEventListener("resize", setInitial);
        return () => window.removeEventListener("resize", setInitial);
    }, []);

    // Each divider's limits for a given size, shared by dragging and the keyboard.
    const colLimits = (width) => ({ min: 280, max: width - 340 });
    const rowLimits = (height) => ({ min: 160, max: height - 210 });

    // Drag the vertical border → resize the Lesson column.
    const startColResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = colLimits(rect.width);
            setLeftWidth(clampSize(ev.clientX - rect.left, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // Drag the horizontal border → resize the Concept Canvas vs. the Prompt Lab.
    const startRowResize = (e) => {
        e.preventDefault();
        const col = rightColRef.current;
        if (!col) return;
        const rect = col.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = rowLimits(rect.height);
            setCanvasHeight(clampSize(ev.clientY - rect.top, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-row");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-row");
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages.length, isThinking]);

    // Auto-grow the input like Claude/ChatGPT: reset to one line, then expand to
    // fit the content up to a cap (after which it scrolls internally).
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
    }, [input]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (!isThinking) onSend();
        }
    };

    // Send keeps focus in the composer; the button disables itself once the box
    // is empty, which would otherwise drop focus to <body>.
    const sendFromButton = () => {
        onSend();
        textareaRef.current?.focus();
    };

    // Spoken summaries; the chat log reads the reply itself.
    const [status, announce] = useStatus();
    useOnChange(!!isThinking, (thinking) => {
        if (thinking) return announce(t("promptLab.statusReplying"));
        announce(t("promptLab.statusReplied"));
        refocusIfLost(textareaRef.current);
    });

    return (
        <div ref={containerRef} className="prompt-lab-root canvas-layout-root">
            {/* LEFT: Lesson */}
            <div
                className="prompt-lab-pane prompt-lab-pane--lesson canvas-layout-lesson"
                style={{ width: leftWidth }}
            >
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">{lessonTitle || definition?.title || t("promptLab.lessonFallback")}</CardTitle>
                            <ListenButton text={definition?.lessonMarkdown} lessonID={definition?.lessonID} />
                        </CardHeader>
                        <CardBody className="prompt-lab-lesson-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {definition?.lessonMarkdown || ""}
                            </ReactMarkdown>

                            {definition?.exercisePrompt && (
                                <div className="prompt-lab-exercise">
                                    <strong>{t("promptLab.yourExercise")}</strong>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {definition.exercisePrompt}
                                    </ReactMarkdown>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Draggable border: Lesson ↔ studio column */}
            <PaneResizer
                value={leftWidth}
                {...colLimits(containerRef.current?.clientWidth || window.innerWidth)}
                onResize={setLeftWidth}
                onPointerDown={startColResize}
                label={t("promptLab.resizePane", { pane: t("promptLab.lessonFallback") })}
                title={t("promptLab.dragToResize", "Drag to resize")}
            />

            {/* RIGHT: Concept Canvas (exercise, top) + Prompt Lab (optional helper, bottom) */}
            <div ref={rightColRef} className="prompt-lab-pane prompt-lab-pane--right canvas-layout-right">
                {/* Canvas — the interactive exercise */}
                <div className="canvas-layout-canvas" style={{ height: canvasHeight }}>
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">{t("promptLab.conceptCanvas")}</CardTitle>
                            {allPassed && (
                                <span className="canvas-layout-done">
                                    <FontAwesomeIcon icon={faCircleCheck} /> {t("promptLab.complete")}
                                </span>
                            )}
                        </CardHeader>
                        <CardBody className="canvas-layout-canvas-body">
                            <ConceptCanvas
                                canvas={definition?.canvas}
                                onComplete={onComplete}
                                initiallyComplete={allPassed}
                                onStateChange={onCanvasStateChange}
                                solveSignal={solveSignal}
                            />
                        </CardBody>
                    </Card>
                </div>

                {/* Draggable border: Concept Canvas ↔ Prompt Lab */}
                <PaneResizer
                    orientation="horizontal"
                    value={canvasHeight}
                    {...rowLimits(rightColRef.current?.clientHeight || window.innerHeight)}
                    onResize={setCanvasHeight}
                    onPointerDown={startRowResize}
                    label={t("promptLab.resizePane", { pane: t("promptLab.conceptCanvas") })}
                    title={t("promptLab.dragToResize", "Drag to resize")}
                />

                {/* Prompt Lab — optional helper, never grades */}
                <div className="canvas-layout-chat">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header canvas-layout-chat-header">
                            <CardTitle tag="h5" className="mb-0">{t("promptLab.promptLabShort")}</CardTitle>
                            <span className="canvas-layout-chat-sub">{t("promptLab.askAnythingOptional")}</span>
                        </CardHeader>
                        {/* A log, so new messages are read out. The "Thinking…" bubble
                            is hidden from it: the status line says Claude is replying. */}
                        <CardBody className="prompt-lab-chat-messages" role="log" aria-live="polite" aria-relevant="additions">
                            {messages.length === 0 && (
                                <div className="prompt-lab-chat-empty">
                                    {t("promptLab.canvasChatEmpty")}
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div key={i} className={`prompt-lab-msg prompt-lab-msg--${m.role}`}>
                                    <div className="prompt-lab-msg-role">{m.role === "user" ? t("promptLab.you") : "Claude"}</div>
                                    <div className="prompt-lab-msg-content">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                                    </div>
                                </div>
                            ))}
                            {isThinking && (
                                <div className="prompt-lab-msg prompt-lab-msg--assistant prompt-lab-msg--thinking" aria-hidden="true">
                                    <div className="prompt-lab-msg-role">Claude</div>
                                    <div className="prompt-lab-msg-content">{t("promptLab.thinking")}</div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </CardBody>
                        <div className="prompt-lab-chat-input">
                            <AiFirstUseNotice />
                            <div className="canvas-chat-inputbox">
                                {/* Read-only, not disabled, while Claude replies, so
                                    keyboard focus stays here. */}
                                <textarea
                                    ref={textareaRef}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t("promptLab.askLessonPlaceholder")}
                                    rows={1}
                                    aria-label={t("promptLab.messageLabel")}
                                    aria-describedby={sendHintId}
                                    readOnly={isThinking}
                                    aria-busy={!!isThinking}
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    className="canvas-chat-send"
                                    onClick={sendFromButton}
                                    disabled={isThinking || !input?.trim()}
                                    aria-label={t("promptLab.send")}
                                    title={t("promptLab.send")}
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                </Button>
                            </div>
                            <div id={sendHintId} className="pl-chat-hint">{t("promptLab.sendShortcutHint")}</div>
                            <AiMistakesNote />
                        </div>
                    </Card>
                </div>
            </div>

            <LiveStatus message={status} />
        </div>
    );
};

export default CanvasLayout;
